import { useEffect, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support'

interface Insets {
  top: number
  bottom: number
  left: number
  right: number
}

export default function useNavigationBar() {
  const [insets, setInsets] = useState<Insets | null>(null)

  useEffect(() => {
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
      const initializeEdgeToEdge = async () => {
        try {
          // Enable edge-to-edge
          await EdgeToEdge.enable()

          // Get the current insets
          const currentInsets = await EdgeToEdge.getInsets()
          setInsets(currentInsets)

          // Apply CSS variables to use in CSS
          const applyInsets = (insets: Insets) => {
            document.documentElement.style.setProperty(
              '--safe-area-inset-top',
              `${insets.top}px`,
            )
            document.documentElement.style.setProperty(
              '--safe-area-inset-bottom',
              `${insets.bottom}px`,
            )
            document.documentElement.style.setProperty(
              '--safe-area-inset-left',
              `${insets.left}px`,
            )
            document.documentElement.style.setProperty(
              '--safe-area-inset-right',
              `${insets.right}px`,
            )

            // Also apply padding-bottom specifically for the navigation bar
            if (insets.bottom > 0) {
              document.documentElement.style.setProperty(
                '--navigation-bar-height',
                `${insets.bottom}px`,
              )
              // Apply to all ion-content elements
              const ionContents = document.querySelectorAll('ion-content')
              ionContents.forEach((content) => {
                ;(content as HTMLElement).style.setProperty(
                  'padding-bottom',
                  `${insets.bottom}px`,
                  'important',
                )
              })
              console.log(
                `Edge-to-edge enabled: bottom inset=${insets.bottom}px, applied to ${ionContents.length} ion-content elements`,
              )
            } else {
              document.documentElement.style.removeProperty(
                '--navigation-bar-height',
              )
              const ionContents = document.querySelectorAll('ion-content')
              ionContents.forEach((content) => {
                ;(content as HTMLElement).style.removeProperty('padding-bottom')
              })
            }
          }

          applyInsets(currentInsets)

          // Listener for orientation or window size changes
          const handleResize = async () => {
            try {
              const newInsets = await EdgeToEdge.getInsets()
              setInsets(newInsets)
              applyInsets(newInsets)
            } catch (error) {
              console.error('Error getting insets on resize:', error)
            }
          }

          window.addEventListener('resize', handleResize)
          window.addEventListener('orientationchange', handleResize)

          return () => {
            window.removeEventListener('resize', handleResize)
            window.removeEventListener('orientationchange', handleResize)
          }
        } catch (error) {
          console.error('Error initializing edge-to-edge:', error)
        }
      }

      initializeEdgeToEdge()
    }
  }, [])

  return insets
}
